import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Lock, Pin } from 'lucide-react'
import FieldWrapper from './FieldWrapper'
import JsonPreview from './JsonPreview'
import { useCallback, memo } from 'react'

/**
 * @typedef {Object} SortableBlockProps
 * @property {Object} block - The block data
 * @property {number} index - The block's position index
 * @property {boolean} isSelected - Whether the block is currently selected
 * @property {Function} onSelect - Callback when block is selected
 * @property {'draft'|'live'} viewMode - The current view mode
 * @property {Function} onFieldChange - Callback when field value changes
 * @property {Object} draftStates - The current draft states for all blocks
 * @property {string} locale - The current locale
 * @property {boolean} readOnly - Whether the block is read-only
 */

/**
 * @param {SortableBlockProps} props
 */
const SortableBlock = memo(
  function SortableBlock({
    block,
    index,
    isSelected,
    onSelect,
    viewMode = 'draft',
    onFieldChange,
    draftStates,
    locale = 'en',
    readOnly = false,
  }) {
    const isFixed = block.is_fixed || false

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: block.id, disabled: isFixed })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }

    const handleFieldChange = useCallback(
      (fieldId, value) => {
        if (onFieldChange) {
          onFieldChange(block.id, fieldId, value)
        }
      },
      [onFieldChange, block.id]
    )

    const getCurrentContent = () => {
      const baseContent = block.content || {}
      if (viewMode === 'draft') {
        const revisionContent =
          block.latest_revision?.content &&
          typeof block.latest_revision.content === 'object'
            ? block.latest_revision.content
            : {}
        const localDraft = draftStates[block.id] || {}
        return { ...baseContent, ...revisionContent, ...localDraft }
      }
      return baseContent
    }

    const currentContent = getCurrentContent()

    const getBorderClass = () => {
      if (isFixed) {
        return isSelected 
          ? 'border-amber-500 shadow-lg' 
          : 'border-amber-300 hover:border-amber-400'
      }
      return isSelected
        ? 'border-blue-500 shadow-lg'
        : 'border-gray-200 hover:border-gray-300'
    }

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`rounded-lg border-2 transition-all cursor-pointer ${
          isFixed ? 'bg-amber-50' : 'bg-white'
        } ${getBorderClass()} ${readOnly ? 'opacity-60' : ''}`}
        onClick={() => onSelect(block)}
      >
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                {!isFixed && (
                  <div
                    className="flex items-center gap-2 cursor-grab active:cursor-grabbing"
                    {...attributes}
                    {...listeners}
                  >
                    <GripVertical className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-500">
                      #{index + 1}
                    </span>
                  </div>
                )}
                {isFixed && (
                  <span className="text-sm font-medium text-gray-500">
                    #{index + 1}
                  </span>
                )}
                <h3 className="text-lg font-semibold text-gray-900">
                  {block.block_type?.name || 'Unknown Type'}
                </h3>
                {isFixed && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    <Pin className="h-3 w-3" />
                    Fixed
                  </span>
                )}
                {readOnly && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    <Lock className="h-3 w-3 mr-1" />
                    Read-only
                  </span>
                )}
                {block.has_revision && !readOnly && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Draft
                  </span>
                )}
              </div>

              <div className="text-sm text-gray-600 mb-3">
                {block.block_type?.description || 'No description available'}
              </div>

              <div className="space-y-4">
                {block.block_type?.schema?.fields?.length > 0 ? (
                  block.block_type.schema.fields.map((field) => (
                    <FieldWrapper
                      key={`${field.id}-${block.id}-${locale}`}
                      field={field}
                      block={block}
                      viewMode={viewMode}
                      draftStates={draftStates}
                      locale={locale}
                      onFieldChange={handleFieldChange}
                      readOnly={readOnly}
                    />
                  ))
                ) : (
                  <div className="text-sm text-gray-400 italic">
                    No fields configured for this block type
                  </div>
                )}

                <JsonPreview
                  content={currentContent}
                  title={`${viewMode === 'draft' ? 'Draft' : 'Live'} Content`}
                  defaultExpanded={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    if (
      prevProps.block.id !== nextProps.block.id ||
      prevProps.index !== nextProps.index ||
      prevProps.isSelected !== nextProps.isSelected ||
      prevProps.viewMode !== nextProps.viewMode ||
      prevProps.locale !== nextProps.locale ||
      prevProps.readOnly !== nextProps.readOnly
    ) {
      return false
    }

    const prevDraft = prevProps.draftStates[prevProps.block.id]
    const nextDraft = nextProps.draftStates[nextProps.block.id]

    if (JSON.stringify(prevDraft) !== JSON.stringify(nextDraft)) {
      return false
    }

    if (
      JSON.stringify(prevProps.block.latest_revision) !==
      JSON.stringify(nextProps.block.latest_revision)
    ) {
      return false
    }

    if (
      JSON.stringify(prevProps.block.content) !==
      JSON.stringify(nextProps.block.content)
    ) {
      return false
    }

    return true
  }
)

export default SortableBlock
